import type { Id } from "../primitives";

/**
 * The Update stock screen (#602): load counts and corrections for one
 * warehouse as lines of "SKU, how, number", review once, apply once.
 *
 * Nothing here talks to Shopify. A line is a request for a *new number*; the
 * server turns it into a delta against the figure it reads at apply time and
 * sends it through the one stock-adjustment write path (`createStockAdjustment`),
 * so Shopify accepts first and the local row is written from its answer.
 */

/** How the number on a line is read. */
export const STOCK_UPDATE_MODES = ["set", "add", "remove"] as const;
export type StockUpdateMode = (typeof STOCK_UPDATE_MODES)[number];

export const STOCK_UPDATE_MODE_LABELS: Record<StockUpdateMode, string> = {
  set: "Set to",
  add: "Add",
  remove: "Remove",
};

/**
 * The reasons the screen offers. The value is what `stock_adjustments.reason`
 * keeps (the ERP's own word); `ADJUSTMENT_REASON_LABELS` words each one.
 * `damaged_or_lost`, offered before #618, is no longer a choice; rows written
 * with it keep their label.
 */
export const STOCK_UPDATE_REASONS = [
  "stock_take",
  "received_from_vendor",
  "damaged",
  "lost",
  "returned_to_shelf",
  "correction",
] as const;
export type StockUpdateReason = (typeof STOCK_UPDATE_REASONS)[number];

/** The most lines one apply may carry. */
export const STOCK_UPDATE_MAX_LINES = 200;

/** The number a line asks for, in whole units, never negative or fractional. */
export function isStockQuantity(value: unknown): value is number {
  return (
    typeof value === "number" &&
    Number.isInteger(value) &&
    value >= 0 &&
    value <= 1_000_000
  );
}

/** What the stock becomes if the line is applied to `current`. */
export function stockUpdateResult(
  mode: StockUpdateMode,
  current: number,
  quantity: number,
): number {
  if (mode === "set") return quantity;
  return mode === "add" ? current + quantity : current - quantity;
}

/** The signed delta the write sends: new minus current. Zero means nothing to do. */
export function stockUpdateDelta(
  mode: StockUpdateMode,
  current: number,
  quantity: number,
): number {
  return stockUpdateResult(mode, current, quantity) - current;
}

/** A SKU and a number from a pasted list. */
export interface PastedStockLine {
  sku: string;
  quantity: number;
}

/**
 * Reads a pasted list: one `SKU number` per line, separated by a comma, a
 * semicolon, a tab or spaces. A line that is not a SKU and a whole number comes
 * back in `unreadable`, as written, so it can be left in the box.
 */
export function parsePastedStockList(text: string): {
  lines: PastedStockLine[];
  unreadable: string[];
} {
  const lines: PastedStockLine[] = [];
  const unreadable: string[] = [];
  for (const raw of text.split(/\r?\n/)) {
    const row = raw.trim();
    if (row === "") continue;
    const parts = row.split(/[\s,;]+/).filter((part) => part !== "");
    const quantity = Number(parts[1]);
    if (parts.length !== 2 || !/^\d+$/.test(parts[1] ?? "")) {
      unreadable.push(raw);
      continue;
    }
    lines.push({ sku: (parts[0] ?? "").toUpperCase(), quantity });
  }
  return { lines, unreadable };
}

/** A stock level the screen can choose, find by SKU and show the current number of. */
export interface StockLookupRow {
  variantId: Id;
  sku: string | null;
  productTitle: string;
  variantTitle: string | null;
  /** What the ERP holds now at the warehouse; may be below zero. */
  available: number;
}

export interface StockLookupQuery {
  warehouseId: Id;
  /** Matches the SKU or the product title. */
  search?: string;
  /** Exact SKUs, matched ignoring case. */
  skus?: readonly string[];
  /** The lowest-stocked N instead of a search. */
  lowest?: number;
}

export interface StockUpdateLineInput {
  variantId: Id;
  mode: StockUpdateMode;
  quantity: number;
}

export interface StockUpdateInput {
  warehouseId: Id;
  reason: StockUpdateReason;
  /**
   * One per press of Apply, made by the screen and kept until an answer comes
   * back. Sending the same one again (a retry after a lost answer) returns what
   * the first send did and writes nothing twice.
   */
  applyId: string;
  lines: StockUpdateLineInput[];
}

export type StockUpdateLineStatus = "applied" | "unchanged" | "rejected";

export interface StockUpdateLineResult {
  variantId: Id;
  sku: string | null;
  status: StockUpdateLineStatus;
  /** The number before; null when the line never found a stock level. */
  before: number | null;
  /** The number now (the figure Shopify answered with when applied). */
  after: number | null;
  /** Why a line was refused, in Shopify's or the server's words. */
  message: string | null;
  adjustmentId: Id | null;
  /** True when this apply id had already written this line. */
  replayed: boolean;
}

export interface StockUpdateResult {
  lines: StockUpdateLineResult[];
  applied: number;
  unchanged: number;
  rejected: number;
}
